import { useCallback, useEffect, useLayoutEffect, useState, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { FullPageError } from '@nextleap-al/admin-ui';

import { ApiError, api, setUnauthenticatedHandler } from './api/client';
import type { Me } from './api/types';
import { Shell } from './components/Shell';
import { rememberSession } from './lib/boot';
import { ALL_TABS, defaultTab, visible } from './nav';
import { LoginPage } from './pages/Login';
import { AdoptionPanel } from './panels/Adoption';
import { DocumentsPanel } from './panels/Documents';
import { GapsPanel } from './panels/Gaps';
import { HealthPanel } from './panels/Health';
import { IntegrationDetailPanel } from './panels/IntegrationDetail';
import { IntegrationsPanel } from './panels/Integrations';
import { OfftopicPanel } from './panels/Offtopic';
import { QualityPanel } from './panels/Quality';
import { SummaryPanel } from './panels/Summary';
import { TicketsPanel } from './panels/Tickets';
import { UsersPanel } from './panels/Users';
import { AppProvider, useApp } from './state/app';

/** Keyed by the tab id in `nav.tsx`, which is also the route. */
const PANELS: Record<string, ComponentType> = {
  summary: SummaryPanel,
  gaps: GapsPanel,
  documents: DocumentsPanel,
  adoption: AdoptionPanel,
  quality: QualityPanel,
  tickets: TicketsPanel,
  offtopic: OfftopicPanel,
  health: HealthPanel,
  users: UsersPanel,
  integrations: IntegrationsPanel,
};

/**
 * A panel that is not in this account's rail is refused the same way an unknown one is, so
 * `/users` typed by an admin lands on their dashboard rather than on a panel that 403s.
 */
function Panels() {
  const { me, simple } = useApp();
  const context = { simple, superadmin: me.is_superadmin };
  const fallback = `/${defaultTab(simple)}`;

  return (
    <Routes>
      <Route element={<Shell />}>
        {ALL_TABS.map((tab) => {
          const Panel = PANELS[tab.id];
          return (
            <Route
              key={tab.id}
              path={`/${tab.id}`}
              element={
                Panel && visible(tab, context) ? <Panel /> : <Navigate to={fallback} replace />
              }
            />
          );
        })}
        {/* One system's own page, under the tab that lists them. Guarded by the same rule as
            the tab itself — and, as there, the guard here is tidiness: every route the page
            calls re-checks the role on the server. */}
        <Route
          path="/integrations/:id"
          element={
            me.is_superadmin ? <IntegrationDetailPanel /> : <Navigate to={fallback} replace />
          }
        />
        {/* The corpus was three tabs before it became one page. A bookmark for any of them
            lands on the page that now holds it, rather than on the generic fallback. */}
        {['/coverage', '/corpus', '/impact'].map((path) => (
          <Route key={path} path={path} element={<Navigate to="/documents" replace />} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to={fallback} replace />} />
    </Routes>
  );
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'anonymous' | 'error'>('loading');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setMe(await api<Me>('/api/me'));
      rememberSession(true);
      setState('ready');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        rememberSession(false);
        setState('anonymous');
        return;
      }
      // The flag is left alone here: a service that is briefly unreachable is not a session
      // that ended, and clearing it would cost the next reload its frame for nothing.
      setError(err instanceof Error ? err.message : String(err));
      setState('error');
    }
  }, []);

  useEffect(() => {
    // A dead session anywhere on the page swaps the whole app for the sign-in form; fetch()
    // cannot follow a redirect to an HTML page.
    setUnauthenticatedHandler(() => {
      rememberSession(false);
      setState('anonymous');
    });
    void load();
  }, [load]);

  // The frame drawn by index.html stands in for the app until there is something real to
  // put in its place. Removed in a layout effect rather than an effect, so it goes in the
  // same paint as its replacement and the two are never on screen together.
  useLayoutEffect(() => {
    if (state !== 'loading') document.getElementById('boot')?.remove();
  }, [state]);

  // Nothing, rather than a spinner: the frame in the HTML is already on the screen, and a
  // centred spinner in place of the page was the thing that read as another page loading.
  if (state === 'loading') return null;
  if (state === 'anonymous') return <LoginPage />;
  if (state === 'error' || !me) {
    return <FullPageError message={`Could not read this account (${error}).`} onRetry={load} />;
  }

  return (
    <AppProvider me={me}>
      <Panels />
    </AppProvider>
  );
}
